import { useState } from "react";
import { ChevronDown, ChevronRight, FolderPlus, GripVertical, MoreVertical, NotebookPen, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { noteDisplayTitle, type Note, type NoteFolder, type NoteFolderNode } from "@/lib/composerNotes";

export const NOTE_DRAG_MIME = "application/x-excerptus-note";
const FOLDER_MIME = "application/x-excerptus-note-folder";

export type FocusTarget = { kind: "root" } | { kind: "folder"; folderId: string };

type Props = {
  tree: NoteFolderNode[];
  notes: Note[]; // ALL notes; rendered inline per folder
  search: string;
  focused: FocusTarget;
  onFocus: (t: FocusTarget) => void;
  onOpenNote: (note: Note) => void;
  onCreateFolder: (parent: NoteFolder | null) => void;
  onRenameFolder: (folder: NoteFolder) => void;
  onDeleteFolder: (folder: NoteFolder) => void;
  onMoveNote: (noteId: string, folderId: string | null) => void;
  onMoveFolder: (folderId: string, newParentId: string | null) => void;
  onReorderNote: (noteId: string, newSortOrder: number) => void;
};

const sortNotes = (a: Note, b: Note): number => {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.updated_at < b.updated_at ? 1 : -1;
};

const matchesSearch = (n: Note, q: string): boolean => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    (n.title ?? "").toLowerCase().includes(needle) ||
    (n.body ?? "").toLowerCase().includes(needle)
  );
};

export const NotesFolderTree = ({
  tree,
  notes,
  search,
  focused,
  onFocus,
  onOpenNote,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveNote,
  onMoveFolder,
  onReorderNote,
}: Props) => {
  const rootNotes = notes
    .filter((n) => !n.folder_id && matchesSearch(n, search))
    .sort(sortNotes);

  return (
    <div className="space-y-0.5">
      <RootRow
        rootNotes={rootNotes}
        focused={focused}
        search={search}
        onFocus={onFocus}
        onOpenNote={onOpenNote}
        onMoveNote={onMoveNote}
        onMoveFolder={onMoveFolder}
        onReorderNote={onReorderNote}
      />
      {tree.map((node) => (
        <FolderBranch
          key={node.id}
          node={node}
          depth={0}
          notes={notes}
          search={search}
          focused={focused}
          onFocus={onFocus}
          onOpenNote={onOpenNote}
          onCreateFolder={onCreateFolder}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
          onMoveNote={onMoveNote}
          onMoveFolder={onMoveFolder}
          onReorderNote={onReorderNote}
        />
      ))}
    </div>
  );
};

// ---- root ----

const RootRow = ({
  rootNotes,
  focused,
  search,
  onFocus,
  onOpenNote,
  onMoveNote,
  onMoveFolder,
  onReorderNote,
}: {
  rootNotes: Note[];
  focused: FocusTarget;
  search: string;
  onFocus: (t: FocusTarget) => void;
  onOpenNote: (note: Note) => void;
  onMoveNote: (noteId: string, folderId: string | null) => void;
  onMoveFolder: (folderId: string, newParentId: string | null) => void;
  onReorderNote: (noteId: string, newSortOrder: number) => void;
}) => {
  // Auto-open when searching and at least one root note matches.
  const [openState, setOpenState] = useState(true);
  const open = search ? rootNotes.length > 0 : openState;
  const [hover, setHover] = useState(false);
  const isFocused = focused.kind === "root";

  return (
    <div>
      <div
        onDragOver={(e) => {
          if (
            !e.dataTransfer.types.includes(NOTE_DRAG_MIME) &&
            !e.dataTransfer.types.includes(FOLDER_MIME)
          ) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setHover(true);
        }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => {
          setHover(false);
          const notePayload = e.dataTransfer.getData(NOTE_DRAG_MIME);
          const folderPayload = e.dataTransfer.getData(FOLDER_MIME);
          if (notePayload) {
            e.preventDefault();
            try {
              const { id } = JSON.parse(notePayload) as { id: string };
              if (id) onMoveNote(id, null);
            } catch { /* noop */ }
          } else if (folderPayload) {
            e.preventDefault();
            try {
              const { id } = JSON.parse(folderPayload) as { id: string };
              if (id) onMoveFolder(id, null);
            } catch { /* noop */ }
          }
        }}
        className={cn(
          "group relative flex items-center gap-1 rounded-md px-1.5 py-1 text-sm font-medium",
          isFocused ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
          hover && "ring-1 ring-primary/60 bg-primary/5",
        )}
      >
        <button
          type="button"
          onClick={() => {
            setOpenState((v) => !v);
            onFocus({ kind: "root" });
          }}
          className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <NotebookPen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <button
          type="button"
          onClick={() => {
            onFocus({ kind: "root" });
            setOpenState(true);
          }}
          className="flex-1 truncate text-left"
        >
          Notes
          {rootNotes.length > 0 && (
            <span className="ml-1.5 text-[10px] text-muted-foreground">{rootNotes.length}</span>
          )}
        </button>
      </div>
      {open && (
        <NoteList
          notes={rootNotes}
          depth={1}
          folderId={null}
          search={search}
          onOpenNote={onOpenNote}
          onMoveNote={onMoveNote}
          onReorderNote={onReorderNote}
        />
      )}
    </div>
  );
};

// ---- folder branch ----

const FolderBranch = ({
  node,
  depth,
  notes,
  search,
  focused,
  onFocus,
  onOpenNote,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveNote,
  onMoveFolder,
  onReorderNote,
}: {
  node: NoteFolderNode;
  depth: number;
  notes: Note[];
  search: string;
  focused: FocusTarget;
  onFocus: (t: FocusTarget) => void;
  onOpenNote: (note: Note) => void;
  onCreateFolder: (parent: NoteFolder | null) => void;
  onRenameFolder: (folder: NoteFolder) => void;
  onDeleteFolder: (folder: NoteFolder) => void;
  onMoveNote: (noteId: string, folderId: string | null) => void;
  onMoveFolder: (folderId: string, newParentId: string | null) => void;
  onReorderNote: (noteId: string, newSortOrder: number) => void;
}) => {
  const folderNotes = notes
    .filter((n) => n.folder_id === node.id && matchesSearch(n, search))
    .sort(sortNotes);

  // Recursively check if descendants have matches when searching
  const descendantHasMatch = (n: NoteFolderNode): boolean => {
    const direct = notes.some((nt) => nt.folder_id === n.id && matchesSearch(nt, search));
    if (direct) return true;
    return n.children.some(descendantHasMatch);
  };

  const [openState, setOpenState] = useState(true);
  const open = search ? (folderNotes.length > 0 || node.children.some(descendantHasMatch)) : openState;

  const [hover, setHover] = useState(false);
  const isFocused = focused.kind === "folder" && focused.folderId === node.id;

  return (
    <div>
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(FOLDER_MIME, JSON.stringify({ id: node.id }));
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          if (
            !e.dataTransfer.types.includes(NOTE_DRAG_MIME) &&
            !e.dataTransfer.types.includes(FOLDER_MIME)
          ) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setHover(true);
        }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => {
          setHover(false);
          const notePayload = e.dataTransfer.getData(NOTE_DRAG_MIME);
          const folderPayload = e.dataTransfer.getData(FOLDER_MIME);
          if (notePayload) {
            e.preventDefault();
            e.stopPropagation();
            try {
              const { id } = JSON.parse(notePayload) as { id: string };
              if (id) onMoveNote(id, node.id);
              setOpenState(true);
            } catch { /* noop */ }
            return;
          }
          if (folderPayload) {
            e.preventDefault();
            e.stopPropagation();
            try {
              const { id } = JSON.parse(folderPayload) as { id: string };
              if (id && id !== node.id) {
                onMoveFolder(id, node.id);
                setOpenState(true);
              }
            } catch { /* noop */ }
          }
        }}
        className={cn(
          "group relative flex items-center gap-1 rounded-md px-1.5 py-1 text-sm",
          isFocused ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
          hover && "ring-1 ring-primary/60 bg-primary/5",
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        <button
          type="button"
          onClick={() => {
            setOpenState((v) => !v);
            onFocus({ kind: "folder", folderId: node.id });
          }}
          className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => {
            onFocus({ kind: "folder", folderId: node.id });
            setOpenState(true);
          }}
          className="flex-1 truncate text-left"
          title={node.name}
        >
          {node.name}
          {folderNotes.length > 0 && (
            <span className="ml-1.5 text-[10px] text-muted-foreground">{folderNotes.length}</span>
          )}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
              aria-label="Folder actions"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onSelect={() => onCreateFolder(node)}>
              <FolderPlus className="mr-2 h-4 w-4" /> New sub-folder
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onRenameFolder(node)}>
              <Pencil className="mr-2 h-4 w-4" /> Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onDeleteFolder(node)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {open && (
        <>
          <NoteList
            notes={folderNotes}
            depth={depth + 1}
            folderId={node.id}
            search={search}
            onOpenNote={onOpenNote}
            onMoveNote={onMoveNote}
            onReorderNote={onReorderNote}
          />
          {node.children.map((child) => (
            <FolderBranch
              key={child.id}
              node={child}
              depth={depth + 1}
              notes={notes}
              search={search}
              focused={focused}
              onFocus={onFocus}
              onOpenNote={onOpenNote}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onMoveNote={onMoveNote}
              onMoveFolder={onMoveFolder}
              onReorderNote={onReorderNote}
            />
          ))}
        </>
      )}
    </div>
  );
};

// ---- notes list (drag/drop between notes) ----

const NoteList = ({
  notes,
  depth,
  folderId,
  search,
  onOpenNote,
  onMoveNote,
  onReorderNote,
}: {
  notes: Note[];
  depth: number;
  folderId: string | null;
  search: string;
  onOpenNote: (note: Note) => void;
  onMoveNote: (noteId: string, folderId: string | null) => void;
  onReorderNote: (noteId: string, newSortOrder: number) => void;
}) => {
  if (notes.length === 0) {
    return (
      <p
        className="px-2 py-1 text-[11px] italic text-muted-foreground"
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {search ? "No matching notes." : "No notes yet."}
      </p>
    );
  }
  return (
    <ul className="space-y-0.5">
      {notes.map((n, idx) => (
        <NoteRow
          key={n.id}
          note={n}
          depth={depth}
          targetFolderId={folderId}
          siblings={notes}
          index={idx}
          onOpenNote={onOpenNote}
          onMoveNote={onMoveNote}
          onReorderNote={onReorderNote}
        />
      ))}
    </ul>
  );
};

const NoteRow = ({
  note,
  depth,
  targetFolderId,
  siblings,
  index,
  onOpenNote,
  onMoveNote,
  onReorderNote,
}: {
  note: Note;
  depth: number;
  targetFolderId: string | null;
  siblings: Note[];
  index: number;
  onOpenNote: (note: Note) => void;
  onMoveNote: (noteId: string, folderId: string | null) => void;
  onReorderNote: (noteId: string, newSortOrder: number) => void;
}) => {
  const [dropEdge, setDropEdge] = useState<"top" | "bottom" | null>(null);
  return (
    <li>
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(
            NOTE_DRAG_MIME,
            JSON.stringify({ id: note.id, folderId: note.folder_id ?? null }),
          );
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes(NOTE_DRAG_MIME)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          const rect = e.currentTarget.getBoundingClientRect();
          const isTop = e.clientY < rect.top + rect.height / 2;
          setDropEdge(isTop ? "top" : "bottom");
        }}
        onDragLeave={() => setDropEdge(null)}
        onDrop={(e) => {
          const edge = dropEdge;
          setDropEdge(null);
          const notePayload = e.dataTransfer.getData(NOTE_DRAG_MIME);
          if (!notePayload) return;
          e.preventDefault();
          e.stopPropagation();
          try {
            const parsed = JSON.parse(notePayload) as { id: string; folderId?: string | null };
            const draggedId = parsed.id;
            const draggedFolderId = parsed.folderId ?? null;
            if (!draggedId || draggedId === note.id) return;
            // Cross-folder drag: fall back to move into this folder.
            if (draggedFolderId !== targetFolderId) {
              onMoveNote(draggedId, targetFolderId);
              return;
            }
            // Same-folder reorder: compute new sort_order between neighbors.
            const insertAfter = edge === "bottom";
            const anchorIdx = insertAfter ? index : index - 1;
            const beforeNote = anchorIdx >= 0 ? siblings[anchorIdx] : null;
            const afterIdx = anchorIdx + 1;
            // Skip the dragged note itself when picking neighbors.
            const before = beforeNote && beforeNote.id !== draggedId ? beforeNote : (anchorIdx - 1 >= 0 ? siblings[anchorIdx - 1] : null);
            const afterCandidate = siblings[afterIdx];
            const after = afterCandidate && afterCandidate.id !== draggedId ? afterCandidate : siblings[afterIdx + 1] ?? null;
            let newSort: number;
            if (before && after) newSort = (before.sort_order + after.sort_order) / 2;
            else if (before) newSort = before.sort_order + 1000;
            else if (after) newSort = after.sort_order - 1000;
            else return;
            onReorderNote(draggedId, newSort);
          } catch { /* noop */ }
        }}
        onClick={() => onOpenNote(note)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenNote(note);
          }
        }}
        className={cn(
          "group relative flex w-full cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-left text-sm hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
      >
        {dropEdge === "top" && (
          <span className="pointer-events-none absolute inset-x-1 -top-px h-0.5 rounded bg-primary" aria-hidden />
        )}
        {dropEdge === "bottom" && (
          <span className="pointer-events-none absolute inset-x-1 -bottom-px h-0.5 rounded bg-primary" aria-hidden />
        )}
        <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{noteDisplayTitle(note)}</div>
          <div className="truncate text-[11px] text-muted-foreground">
            {new Date(note.updated_at).toLocaleString()}
          </div>
        </div>
      </div>
    </li>
  );
};
